/**
 * Notification Repository
 * Extends BaseRepository for notification data access
 */

import {
    BaseRepository,
    BaseEntity
} from '../domain/services/BaseRepository';
import { COLLECTIONS } from '../domain/collections';
import {
    collection,
    query,
    where,
    orderBy,
    limit as firestoreLimit,
    getDocs,
    writeBatch,
    doc,
    updateDoc,
    increment
} from 'firebase/firestore';
import { db } from '../infrastructure/firebaseConfig';
import { logger } from '../infrastructure/logger';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { Notification as NotificationType } from '../domain/types/notification';

export interface NotificationEntity extends BaseEntity {
    userId: string;
    type: 'order' | 'review' | 'promo' | 'system' | 'wishlist' | 'price_drop' | 'follow' | 'social';
    title: string;
    message: string;
    link?: string;
    data?: Record<string, unknown>;
    isRead: boolean;
    readAt?: string;
    createdAt: string;
}

class NotificationRepository extends BaseRepository<NotificationEntity> {
    protected collectionName = COLLECTIONS.NOTIFICATIONS;

    protected toEntity(doc: QueryDocumentSnapshot<DocumentData>): NotificationEntity {
        const data = doc.data();
        // Handle Firestore Timestamp
        const convertDate = (value: unknown): string => {
            if (!value) return new Date().toISOString();
            if (value instanceof Date) return value.toISOString();
            if (typeof value === 'string') return value;
            if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
                return (value as { toDate: () => Date }).toDate().toISOString();
            }
            return new Date().toISOString();
        };

        return {
            id: doc.id,
            userId: (data.userId as string) || '',
            type: (data.type as NotificationEntity['type']) || 'system',
            title: (data.title as string) || '',
            message: (data.message as string) || '',
            link: data.link as string | undefined,
            data: (data.data || {}) as Record<string, unknown>,
            isRead: !!data.isRead,
            readAt: data.readAt ? convertDate(data.readAt) : undefined,
            createdAt: data.createdAt ? convertDate(data.createdAt) : new Date().toISOString()
        };
    }

    /**
     * Get user notifications with pagination
     */
    async getUserNotifications(
        userId: string,
        limit: number = 50,
        onlyUnread: boolean = false
    ): Promise<NotificationEntity[]> {
        try {
            let q = query(
                this.collection,
                where('userId', '==', userId),
                orderBy('createdAt', 'desc'),
                firestoreLimit(limit)
            );

            if (onlyUnread) {
                q = query(
                    this.collection,
                    where('userId', '==', userId),
                    where('isRead', '==', false),
                    orderBy('createdAt', 'desc'),
                    firestoreLimit(limit)
                );
            }

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => this.toEntity(doc));
        } catch (error) {
            logger.error('[NotificationRepository] Error fetching user notifications', error);
            return [];
        }
    }

    /**
     * Count unread notifications for a user
     */
    async countUnread(userId: string): Promise<number> {
        try {
            const q = query(
                this.collection,
                where('userId', '==', userId),
                where('isRead', '==', false)
            );
            const snapshot = await getDocs(q);
            return snapshot.size;
        } catch (error) {
            logger.error('[NotificationRepository] Error counting unread notifications', error);
            return 0;
        }
    }

    /**
     * Mark a notification as read
     */
    async markAsRead(notificationId: string): Promise<void> {
        try {
            const notifRef = doc(db, this.collectionName, notificationId);
            await updateDoc(notifRef, {
                isRead: true,
                readAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            logger.error('[NotificationRepository] Error marking notification as read', error);
            throw error;
        }
    }

    /**
     * Mark all notifications as read for a user
     */
    async markAllAsRead(userId: string): Promise<void> {
        try {
            const notifications = await this.getUserNotifications(userId, 100, true);

            if (notifications.length === 0) return;

            // Firestore allows max 500 operations per batch
            const batch = writeBatch(db);
            const processed = new Set<string>();

            notifications.forEach(notification => {
                if (processed.has(notification.id)) return;
                processed.add(notification.id);

                const ref = doc(this.collection, notification.id);
                batch.update(ref, {
                    isRead: true,
                    readAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                });
            });

            await batch.commit();
        } catch (error) {
            logger.error('[NotificationRepository] Error marking all as read', error);
            throw error;
        }
    }

    /**
     * Create a new notification
     */
    async createNotification(notification: Omit<NotificationEntity, 'id'>): Promise<string> {
        return this.create(notification);
    }

    /**
     * Delete all notifications for a user
     */
    async deleteAllForUser(userId: string): Promise<number> {
        try {
            const notifications = await this.getUserNotifications(userId, 500);

            if (notifications.length === 0) return 0;

            const batch = writeBatch(db);
            const processed = new Set<string>();

            notifications.forEach(notification => {
                if (processed.has(notification.id)) return;
                processed.add(notification.id);

                const ref = doc(this.collection, notification.id);
                batch.delete(ref);
            });

            await batch.commit();
            return notifications.length;
        } catch (error) {
            logger.error('[NotificationRepository] Error deleting all notifications', error);
            throw error;
        }
    }
}

export const notificationRepository = new NotificationRepository();
